/**
 * src/three/BgMusic.js
 * เพลงพื้นหลังจากไฟล์ MP3 — ลูปด้วย AudioBuffer ไม่รีสตาร์ทตอนยิง
 */
import { AUDIO, fetchAudioBuffer } from './audioFiles.js';
import { decodeAudioData, getAudioContext, resumeAudioContext } from './audioContext.js';

export default class BgMusic {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.enabled = true;
    this.started = false;
    this.intensity = 1;
    this._calm = false;
    this._baseVol = 0.32;
    this.userVolume = 0.20;
    this._resuming = false;
    this._blocked = false;
    this._buffer = null;
    this._src = null;
    this._loadPromise = null;
    this._wantPlay = false;
    this._starting = false;
  }

  ensure() {
    if (this.ctx && this.master) return;
    this.ctx = getAudioContext();
    if (!this.ctx) return;
    if (!this.master) {
      this.master = this.ctx.createGain();
      this.master.gain.value = 0;
      this.master.connect(this.ctx.destination);
    }
    this.ctx.onstatechange = () => {
      if (this.ctx?.state !== 'running' || !this._wantPlay || this._src) return;
      this._spawnSource(false);
    };
  }

  unlock() {
    this._blocked = false;
    this.resume();
  }

  resume() {
    this.ensure();
    if (!this.ctx) return Promise.resolve();
    if (this.ctx.state === 'running') {
      this._blocked = false;
      return Promise.resolve();
    }
    return resumeAudioContext().then(() => {
      this._blocked = this.ctx?.state !== 'running';
    });
  }

  start() {
    this.started = true;
    this._wantPlay = this.enabled;
    const go = () => {
      this.ensure();
      if (!this.ctx) return;
      if (!this.enabled) {
        this.applyVolume();
        return;
      }
      if (this._src) return;
      this._loadAndPlay();
    };
    const p = this.resume();
    if (p && typeof p.then === 'function') p.then(go, go);
    else go();
  }

  preload() {
    if (this._fetchPromise) return this._fetchPromise;
    this._fetchPromise = fetchAudioBuffer(AUDIO.bgm)
      .then((data) => {
        this._raw = data;
        return data;
      })
      .catch(() => {
        this._fetchPromise = null;
        return null;
      });
    return this._fetchPromise;
  }

  _decode() {
    if (this._buffer) return Promise.resolve(this._buffer);
    this.ensure();
    if (!this.ctx) return Promise.resolve(null);
    if (!this._loadPromise) {
      this._loadPromise = this.preload()
        .then((data) => {
          if (this._buffer) return this._buffer;
          if (!data || !this.ctx) return null;
          return decodeAudioData(this.ctx, data);
        })
        .then((buf) => {
          this._buffer = buf;
          return buf;
        })
        .catch(() => {
          this._loadPromise = null;
          return null;
        });
    }
    return this._loadPromise;
  }

  _loadAndPlay() {
    if (this._starting || this._src) return;
    this._starting = true;
    this._decode().then((buf) => {
      this._starting = false;
      if (!buf || !this._wantPlay || this._src || !this.ctx) return;
      this._spawnSource(true);
    }, () => {
      this._starting = false;
    });
  }

  _spawnSource(fadeIn = false) {
    if (!this.ctx || !this._buffer || !this._wantPlay || this._src) return;
    if (this.ctx.state !== 'running') {
      resumeAudioContext().then(() => {
        if (!this._src) this._spawnSource(fadeIn);
      });
      return;
    }
    const src = this.ctx.createBufferSource();
    src.buffer = this._buffer;
    src.loop = true;
    src.loopStart = 0;
    src.loopEnd = Math.max(0, this._buffer.duration - 0.03);
    src.connect(this.master);
    src.onended = () => {
      if (this._src === src) this._src = null;
      if (!this._wantPlay || !this.enabled || !this.ctx || this.ctx.state === 'closed') return;
      this._spawnSource(false);
    };
    try {
      src.start();
    } catch {
      this._src = null;
      return;
    }
    this._src = src;
    this.applyVolume(fadeIn);
  }

  applyVolume(fadeIn = false, snap = false) {
    if (!this.master || !this.ctx) return;
    const t = this.ctx.currentTime;
    const calmMul = this._calm ? 0.38 : 1;
    const target = this.enabled
      ? this._baseVol * this.intensity * calmMul * this.userVolume
      : 0;
    this.master.gain.cancelScheduledValues(t);
    if (fadeIn && this.enabled) {
      this.master.gain.setValueAtTime(0.001, t);
      this.master.gain.exponentialRampToValueAtTime(Math.max(0.002, target), t + 1.4);
    } else {
      this.master.gain.setTargetAtTime(target, t, snap ? 0.04 : 0.28);
    }
  }

  setIntensity(v) {
    this.intensity = Math.max(0.75, Math.min(1.55, v));
    this.applyVolume();
  }

  setCalm(on) {
    this._calm = !!on;
    this.applyVolume();
  }

  getUserVolume() {
    return this.userVolume;
  }

  setUserVolume(v) {
    this.userVolume = Math.max(0, Math.min(1, Number(v) || 0));
    this.enabled = this.userVolume > 0.001;
    this._wantPlay = this.enabled && (this.started || this.userVolume > 0.001);
    if (this.enabled) this.start();
    this.applyVolume(false, true);
  }

  setEnabled(on) {
    this.enabled = !!on;
    this._wantPlay = !!on && this.started;
    if (on) this.start();
    this.applyVolume();
  }

  toggle() {
    this.setEnabled(!this.enabled);
    return this.enabled;
  }

  dispose() {
    this._wantPlay = false;
    if (this._src) this._src.onended = null;
    try { this._src?.stop?.(); } catch { /* ignore */ }
    try { this._src?.disconnect?.(); } catch { /* ignore */ }
    try { this.master?.disconnect?.(); } catch { /* ignore */ }
    this._src = null;
    this._buffer = null;
    this._loadPromise = null;
    this._fetchPromise = null;
    this._raw = null;
    this.master = null;
    this.ctx = null;
    this.started = false;
    this._wantPlay = false;
    this._starting = false;
  }
}
